import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/EmptyState";
import { Truck, Send, Clock, CheckCircle2, AlertTriangle, DollarSign, CreditCard } from "lucide-react";
import api from "../../lib/api";
import { money, fmtDate } from "../../lib/statuses";

export default function VendorClaims() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/vendor-claims").then(({ data }) => setList(data));
    api.get("/admin/vendor-claims/stats").then(({ data }) => setStats(data));
  }, []);

  const CARDS = stats ? [
    { label: "Total Sent", value: stats.total_sent, icon: Send, color: "text-blue-600 bg-blue-50" },
    { label: "Pending", value: stats.pending, icon: Clock, color: "text-amber-600 bg-amber-50" },
    { label: "Approved", value: stats.approved, icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
    { label: "Overdue", value: stats.overdue, icon: AlertTriangle, color: "text-rose-600 bg-rose-50" },
    { label: "Total Claim Value", value: money(stats.total_claim_value), icon: DollarSign, color: "text-slate-600 bg-slate-100" },
    { label: "Approved Credit", value: money(stats.approved_credit), icon: CreditCard, color: "text-purple-600 bg-purple-50" },
  ] : [];

  const decColor = (d) => d === "PENDING" ? "bg-amber-50 text-amber-700" : d === "REJECTED" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700";

  return (
    <AdminLayout title="Vendor Claims">
      <div className="mb-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {!stats ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />) :
          CARDS.map((c) => (
            <div key={c.label} data-testid={`vendor-kpi-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${c.color}`}><c.icon className="h-4 w-4" /></div>
              <div className="font-display text-lg font-bold text-slate-900">{c.value}</div>
              <div className="text-xs text-slate-500">{c.label}</div>
            </div>
          ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>RMA</TableHead><TableHead>Supplier</TableHead><TableHead>Product</TableHead><TableHead>Serial</TableHead>
              <TableHead>Date Sent</TableHead><TableHead className="text-center">Days</TableHead><TableHead>Claim Amount</TableHead>
              <TableHead>Decision</TableHead><TableHead>Last Follow-up</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list === null && Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
              {list?.map((v) => (
                <TableRow key={v.id} data-testid={`vendor-row-${v.id}`} onClick={() => navigate(`/admin/warranty/claims/${v.claim_id}`)} className={`cursor-pointer hover:bg-blue-50/50 ${v.overdue ? "bg-rose-50/40" : ""}`}>
                  <TableCell className="font-mono text-sm font-semibold text-blue-700">{v.rma_number}</TableCell>
                  <TableCell className="text-sm text-slate-700">{v.supplier_name}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-sm text-slate-600">{v.product_name}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{v.serial_number}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(v.date_submitted)}</TableCell>
                  <TableCell className="text-center"><span className={`text-sm font-semibold ${v.overdue ? "text-rose-600" : "text-slate-700"}`}>{v.days_with_vendor}{v.overdue && " ⚠"}</span></TableCell>
                  <TableCell className="font-mono text-sm text-slate-700">{money(v.claim_amount, v.currency)}</TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${decColor(v.vendor_status)}`}>{v.vendor_status}</span></TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(v.last_follow_up)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {list?.length === 0 && <EmptyState icon={Truck} title="No vendor claims yet" description="Escalate a warranty claim to a supplier from its case page." testId="vendor-empty" />}
      </div>
    </AdminLayout>
  );
}
